import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Header,
  PaginationState,
  Row,
  RowData,
  RowSelectionState,
  SortingState,
  Table,
  TableController,
} from '@tanstack/lit-table'
// Extended column definition with textAlign property
export type ExtendedColumnDef<T> = ColumnDef<T, unknown> & {
  textAlign?: string
  headerTextAlign?: string
  disableDefaultSorting?: boolean
}

import clsx from 'clsx'
import { nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { repeat } from 'lit/directives/repeat.js'
import { html, unsafeStatic } from 'lit/static-html.js'

import { DdsElement } from '@/internal/dds-hu-element'
import { t } from '@/localization/localization'

import styles from './datatable.scss?inline'

const MULTISELECT_COLUMN_ID = '__multiSelect__'

// Type alias for sort state
type SortState = false | 'asc' | 'desc'

/**
 * `dap-ds-datatable`
 * @summary A data table is a component that displays data in a tabular format.
 * @element dap-ds-datatable
 * @title - Data table
 * @generic T
 *
 * @event {{ sorting: SortingState }} dds-sorting-change - Fired when the sorting of the table changes.
 * @event {{ selection: RowSelectionState }} dds-selection-change - Fired when the selection of the table changes.
 * @event {{ pagination: PaginationState }} dds-pagination-change - Fired when the pagination of the table changes.
 * @event {{ row: Row<T> }} dds-row-click - Fired when a row is clicked.
 *
 * @csspart base - The main table container.
 * @csspart header - The header of the table.
 * @csspart header-row - The header row of the table.
 * @csspart header-cell - All cells of the header.
 * @csspart body - The body of the table.
 * @csspart row - All rows of the table.
 * @csspart cell - All cells of the table.
 * @csspart pager - The pager of the table.
 *
 * @cssproperty --dds-datatable-bg-color - Background color of the table
 * @cssproperty --dds-datatable-border-color - Border color of the table cells
 * @cssproperty --dds-datatable-header-bg-color - Background color of the table header
 * @cssproperty --dds-datatable-header-text-color - Text color of the table header
 * @cssproperty --dds-datatable-row-hover-bg-color - Background color of hovered rows
 * @cssproperty --dds-datatable-row-selected-bg-color - Background color of selected rows
 * @cssproperty --dds-datatable-cell-padding - Padding of table cells
 * @cssproperty --dds-datatable-header-padding - Padding of header cells
 * @cssproperty --dds-datatable-border-width - Width of table borders
 * @cssproperty --dds-datatable-font-size - Font size of the table
 * @cssproperty --dds-datatable-line-height - Line height of the table
 * @cssproperty --dds-datatable-header-font-weight - Font weight of the header
 * @cssproperty --dds-datatable-stripe-color - Background color for striped rows
 * @cssproperty --dds-datatable-stripe-enabled - Whether to enable striped rows
 * @cssproperty --dds-datatable-border-radius - Border radius of the table
 * @cssproperty --dds-datatable-shadow - Box shadow of the table
 * @cssproperty --dds-datatable-transition-duration - Duration of hover/selection transitions
 * @cssproperty --dds-datatable-z-index - Z-index of the table
 * @cssproperty --dds-datatable-min-height - Minimum height of the table
 * @cssproperty --dds-datatable-max-height - Maximum height of the table
 * @cssproperty --dds-datatable-overflow-x - Horizontal overflow behavior
 * @cssproperty --dds-datatable-overflow-y - Vertical overflow behavior
 */
@customElement('dap-ds-datatable')
export default class DapDSDataTable<T> extends DdsElement {
  static readonly styles = unsafeCSS(styles)

  private readonly tableController = new TableController<T>(this)

  /** Data to display in the table */
  @property({ attribute: false }) data: RowData[] = []
  /** Columns to display in the table */
  @property({ attribute: false }) columns: ExtendedColumnDef<T>[] = []

  /** Row key to use for row selection, this should be a unique key for each row */
  @property() rowKey: string = 'id'

  /** Enable row selection on the table, can be a boolean or a function that returns a boolean
   * @type {boolean | ((row: Row<T>) => boolean)}
   */
  @property({ type: Boolean }) enableRowSelection:
    | boolean
    | ((row: Row<T>) => boolean) = false
  /** Enable sorting on the table */
  @property({ type: Boolean }) enableSorting: boolean = false
  /** Enable manual sorting on the table */
  @property({ type: Boolean }) manualSorting: boolean = false
  /** Enables manual pagination. If this option is set to true, the table will not automatically paginate rows and instead will expect you to manually paginate the rows before passing them to the table. This is useful if you are doing server-side pagination and aggregation. */
  @property({ type: Boolean }) manualPagination: boolean = false
  /** If set to true, pagination will be reset to the first page when page-altering state changes eg. data is updated, filters change, grouping changes, etc. This behavior is automatically disabled when manualPagination is true but it can be overridden by explicitly assigning a boolean value to the autoResetPageIndex table option. */
  @property({ type: Boolean }) autoResetPageIndex: boolean = false

  /** Enable row click on the table */
  @property({ type: Boolean }) enableRowClick: boolean = false

  /** Loading state of the table */
  @property({ type: Boolean }) loading: boolean = false

  /** Whether to enable striped rows */
  @property({ type: Boolean }) enableStripedRows: boolean = false

  /** Number of rows in the table */
  @property({ type: Number }) rowCount?: number

  /** ID of element describing the table */
  @property({ type: String }) ariaDescribedBy?: string
  /** Caption text for the table */
  @property({ type: String }) caption?: string

  /** Whether to show the pager component */
  @property({ type: Boolean }) pager: boolean = false

  /** Show the page size options. */
  @property({ type: String }) showPageSizeOptions = 'true'
  /** Show the page index. */
  @property({ type: String }) showPageIndex = 'true'
  /** Show the page count. */
  @property({ type: String }) showPageCount = 'true'
  /** Show the first button. */
  @property({ type: String }) showFirstButton = 'true'
  /** Show the previous button. */
  @property({ type: String }) showPreviousButton = 'true'
  /** Show the next button. */
  @property({ type: String }) showNextButton = 'true'
  /** Show the last button. */
  @property({ type: String }) showLastButton = 'true'

  /** The label of the first button */
  @property({ type: String }) firstButtonLabel: string
  /** The label of the previous button */
  @property({ type: String }) previousButtonLabel: string
  /** The label of the next button */
  @property({ type: String }) nextButtonLabel: string
  /** The label of the last button */
  @property({ type: String }) lastButtonLabel: string
  /** The function to determine the pager text
   * @type {(pageIndex: number, pageSize: number, totalRows: number) => string}
   */
  @property({ attribute: false }) pageStateText: (
    pageIndex: number,
    pageSize: number,
    totalRows: number,
  ) => string
  /** Available page size options for the pager
   * @type {number[]}
   */
  @property({ type: Array }) pageSizeOptions: number[] = [10, 25, 50, 100]

  /** Sorting state of the table */
  @property({ type: Array }) sorting: SortingState
  /** @ignore */
  @state() private _sorting: SortingState = []

  /** Selection state of the table */
  @property({ type: Object }) rowSelection: RowSelectionState
  /** @ignore */
  @state() private _rowSelection: RowSelectionState = {}

  /** Pagination state of the table */
  @property({ type: Object }) pagination: PaginationState
  /** @ignore */
  @state() private _pagination: PaginationState = {
    pageIndex: 0,
    pageSize: 10,
  }

  /** @ignore */
  @state() private readonly _statusMessage: string = ''

  /** @ignore */
  @property({ type: Boolean }) debug = false

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)

    if (this.sorting) {
      this._sorting = this.sorting
    }

    if (this.rowSelection) {
      this._rowSelection = this.rowSelection
    }

    if (this.pagination) {
      this._pagination = this.pagination
    } else if (this.pageSizeOptions && this.pageSizeOptions.length > 0) {
      // If pagination is not explicitly set, use the first item in pageSizeOptions as default
      this._pagination = {
        ...this._pagination,
        pageSize: this.pageSizeOptions[0],
      }
    }
  }

  protected updated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(_changedProperties)

    if (_changedProperties.has('pagination')) {
      this._pagination = this.pagination
    }

    if (_changedProperties.has('sorting')) {
      this._sorting = this.sorting
    }

    if (
      _changedProperties.has('rowSelection') ||
      _changedProperties.has('rowselection')
    ) {
      this._rowSelection = this.rowSelection
    }

    if (_changedProperties.has('data')) {
      this.requestUpdate()
    }
  }

  private readonly columnsMemo = () => [
    ...(this.enableRowSelection &&
    this.columns[0] &&
    this.columns[0].id !== MULTISELECT_COLUMN_ID
      ? [
          {
            id: MULTISELECT_COLUMN_ID,
            accessorKey: MULTISELECT_COLUMN_ID,
            enableSorting: false,
            size: 12,
            maxSize: 12,
            header: ({ table }: { table: Table<T> }) =>
              html`<dap-ds-checkbox
                isIndeterminate=${table.getIsSomeRowsSelected()}
                @dds-change=${table.getToggleAllRowsSelectedHandler()}
                .checked="${table.getIsAllRowsSelected()}"
                .indeterminate="${table.getIsSomeRowsSelected()}"></dap-ds-checkbox>`,
            cell: ({ row }: { row: any }) =>
              html`<dap-ds-checkbox
                @dds-change="${row.getToggleSelectedHandler()}"
                .checked="${row.getIsSelected()}"
                ?disabled="${!row.getCanSelect()}"
                .indeterminate="${row.getIsSomeSelected()}"></dap-ds-checkbox>`,
          },
        ]
      : []),
    ...this.columns,
  ]

  private renderHeaderCell(header: Header<T, unknown>) {
    const columnDef = header.column.columnDef as ExtendedColumnDef<T>
    const canSort = header.column.getCanSort()
    const isSorted = header.column.getIsSorted()
    const nextSortOrder = header.column.getNextSortingOrder()
    const disableDefaultSorting = columnDef.disableDefaultSorting ?? false

    const headerStyle = this.getHeaderCellStyle(header)
    const contentStyle = columnDef.headerTextAlign
      ? `justify-content: ${columnDef.headerTextAlign};`
      : undefined

    const sortIcon = this.getSortIcon(isSorted)
    const sortTitle = this.getSortTitle(canSort, nextSortOrder)

    return html`
      <th
        colspan="${header.colSpan}"
        rowspan="${header.rowSpan}"
        part="header-cell"
        class="data-table-header-cell"
        scope="col"
        aria-sort=${canSort ? this.getAriaSortValue(isSorted) : 'none'}
        style="${ifDefined(headerStyle)}">
        ${this.renderHeaderCellContent(
          header,
          columnDef,
          canSort,
          disableDefaultSorting,
          sortTitle,
          contentStyle,
          sortIcon,
        )}
      </th>
    `
  }

  private renderHeaderCellContent(
    header: Header<T, unknown>,
    columnDef: ExtendedColumnDef<T>,
    canSort: boolean,
    disableDefaultSorting: boolean,
    sortTitle: string | undefined,
    contentStyle: string | undefined,
    sortIcon: any,
  ) {
    if (!canSort) {
      return flexRender(columnDef.header, header.getContext())
    }

    const elementTag = disableDefaultSorting ? 'div' : 'dap-ds-button'
    const clickHandler = !disableDefaultSorting
      ? header.column.getToggleSortingHandler()
      : null
    const cursorStyle = canSort ? 'pointer' : 'not-allowed'

    return html`<${unsafeStatic(elementTag)}
      class="data-table-header-cell__controller"
      variant="clean"
      title=${ifDefined(sortTitle)}
      @click="${clickHandler}"
      style="cursor: ${cursorStyle}">
      <span 
        part="content" 
        class="data-table-header-cell__content"
        style="${ifDefined(contentStyle)}">
        ${flexRender(columnDef.header, header.getContext())}
        ${sortIcon}
      </span>
    </${unsafeStatic(elementTag)}>`
  }

  private getHeaderCellStyle(header: Header<T, unknown>): string | undefined {
    const columnDef = header.column.columnDef as ExtendedColumnDef<T>
    if (!columnDef.size && !columnDef.maxSize && !columnDef.headerTextAlign) {
      return undefined
    }

    return `
      ${columnDef.size ? `width: ${columnDef.size}px;` : ''}
      ${columnDef.maxSize ? `max-width: ${columnDef.maxSize}px;` : ''}
      ${columnDef.headerTextAlign ? `text-align: ${columnDef.headerTextAlign};` : ''}
    `
  }

  // Replace this union type with a type alias.
  private getSortIcon(isSorted: SortState) {
    if (isSorted === 'asc') {
      return html`<dap-ds-icon-arrow-up-s-line
        size="16"></dap-ds-icon-arrow-up-s-line>`
    }
    if (isSorted === 'desc') {
      return html`<dap-ds-icon-arrow-down-s-line
        size="16"></dap-ds-icon-arrow-down-s-line>`
    }
    return null
  }

  private getSortTitle(
    canSort: boolean,
    nextSortOrder: false | 'asc' | 'desc',
  ): string | undefined {
    if (!canSort) return undefined

    if (nextSortOrder === 'asc') return t('datatable.sortAscending')
    if (nextSortOrder === 'desc') return t('datatable.sortDescending')
    return t('datatable.clearSort')
  }

  private getAriaSortValue(
    isSorted: SortState,
  ): 'ascending' | 'descending' | 'none' {
    if (isSorted === 'asc') return 'ascending'
    if (isSorted === 'desc') return 'descending'
    return 'none'
  }

  private handleRowClick(row: Row<T>): void {
    if (this.enableRowClick) {
      this.emit('dds-row-click', { row })
    }
  }

  private handleRowKeydown(e: KeyboardEvent, row: Row<T>): void {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      this.handleRowClick(row)
    }
  }

  private readonly renderHeader = (table: Table<T>) => {
    return html`
      <thead part="header">
        ${repeat(
          table.getHeaderGroups(),
          headerGroup => headerGroup.id,
          headerGroup => html`
            <tr
              part="header-row"
              class=${clsx('data-table-header-row', {
                'data-table-header-row--striped': this.enableStripedRows,
              })}>
              ${repeat(
                headerGroup.headers,
                header => header.id,
                header => this.renderHeaderCell(header),
              )}
            </tr>
          `,
        )}
      </thead>
    `
  }

  private readonly renderBody = (table: Table<T>) => {
    return html`
      <tbody part="body">
        ${table.getRowModel().rows.map(
          (row, index) => html`
            <tr
              part="row"
              role=${this.enableRowClick ? 'button' : 'row'}
              tabindex=${this.enableRowClick ? 0 : -1}
              aria-selected=${row.getIsSelected()}
              aria-rowindex=${index + 2}
              class=${clsx('data-table-row', {
                'data-table-row--selected': row.getIsSelected(),
                'data-table-row--clickable': this.enableRowClick,
                'data-table-row--striped': this.enableStripedRows,
              })}
              @click=${() => this.handleRowClick(row)}
              @keydown=${(e: KeyboardEvent) => this.handleRowKeydown(e, row)}>
              ${row.getVisibleCells().map(
                cell => html`
                  <td
                    part="cell"
                    class=${clsx('data-table-cell')}
                    style="${ifDefined(
                      (() => {
                        const columnDef = cell.column
                          .columnDef as ExtendedColumnDef<T>
                        return (
                          columnDef.textAlign &&
                          `text-align: ${columnDef.textAlign};`
                        )
                      })(),
                    )}">
                    ${flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                `,
              )}
            </tr>
          `,
        )}
      </tbody>
    `
  }

  protected render() {
    const table = this.tableController.table({
      data: this.data as T[],
      columns: this.columnsMemo(),
      filterFns: {},
      state: {
        sorting: this._sorting,
        rowSelection: this._rowSelection,
        pagination: this._pagination,
      },
      manualSorting: this.manualSorting,
      enableSorting: this.enableSorting,
      enableRowSelection: this.enableRowSelection,
      manualPagination: this.manualPagination,
      autoResetPageIndex: this.autoResetPageIndex,
      onSortingChange: updaterOrValue => {
        if (typeof updaterOrValue === 'function') {
          this._sorting = updaterOrValue(this._sorting)
        } else {
          this._sorting = updaterOrValue
        }

        this.emit('dds-sorting-change', { sorting: this._sorting })
      },
      getCoreRowModel: getCoreRowModel(),
      ...(!this.manualSorting && {
        getSortedRowModel: getSortedRowModel(),
      }),
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: updaterOrValue => {
        if (typeof updaterOrValue === 'function') {
          this._pagination = updaterOrValue(this._pagination)
        } else {
          this._pagination = updaterOrValue
        }

        this.emit('dds-pagination-change', {
          pagination: {
            pageIndex: this._pagination.pageIndex,
            pageSize: this._pagination.pageSize,
          },
        })
      },
      onRowSelectionChange: updaterOrValue => {
        if (typeof updaterOrValue === 'function') {
          this._rowSelection = updaterOrValue(this._rowSelection)
        } else {
          this._rowSelection = updaterOrValue
        }

        this.emit('dds-selection-change', { selection: this._rowSelection })
      },
      getRowId: originalRow => {
        return (originalRow as Record<string, any>)[this.rowKey]
      },
      rowCount: this.rowCount ?? (this.data ? this.data.length : 0),
      debugTable: this.debug,
      debugHeaders: this.debug,
      debugColumns: this.debug,
    })

    return html`
      <table
        part="base"
        role="table"
        aria-label=${ifDefined(
          this.ariaLabel ?? t('datatable.tableDescription'),
        )}
        aria-describedby=${ifDefined(this.ariaDescribedBy)}
        aria-rowcount=${this.rowCount ?? (this.data ? this.data.length : 0)}
        class=${clsx('data-table', {
          'data-table--loading': this.loading,
        })}>
        ${this.caption
          ? html`<caption class="visually-hidden">
              ${this.caption}
            </caption>`
          : nothing}
        ${this.renderHeader(table)} ${this.renderBody(table)}
      </table>
      ${this._statusMessage
        ? html`<div
            aria-live="polite"
            aria-atomic="true"
            class="visually-hidden"
            id="table-status">
            ${this._statusMessage}
          </div>`
        : nothing}
      ${this.pager
        ? html`<dap-ds-pager
            class="data-table-pager"
            part="pager"
            .manualPagination=${this.manualPagination}
            .pageIndex=${table.getState().pagination.pageIndex}
            .pageSize=${table.getState().pagination.pageSize}
            .totalRows=${this.rowCount ?? (this.data ? this.data.length : 0)}
            .pageSizeOptions=${this.pageSizeOptions}
            .showPageSizeOptions=${this.showPageSizeOptions}
            .showPageIndex=${this.showPageIndex}
            .showPageCount=${this.showPageCount}
            .showFirstButton=${this.showFirstButton}
            .showPreviousButton=${this.showPreviousButton}
            .showNextButton=${this.showNextButton}
            .showLastButton=${this.showLastButton}
            .firstButtonLabel=${this.firstButtonLabel}
            .previousButtonLabel=${this.previousButtonLabel}
            .nextButtonLabel=${this.nextButtonLabel}
            .lastButtonLabel=${this.lastButtonLabel}
            pageStateText=${ifDefined(this.pageStateText)}
            exportparts="pager:base, first:base, previous:base, next:base, last:base, page-size"
            @dds-pagination-change=${(e: any) => {
              e.preventDefault()
              e.stopPropagation()

              if (e.detail.action === 'pageSize') {
                table.setPageSize(e.detail.pagination.pageSize)
              } else {
                table.setPageIndex(e.detail.pagination.pageIndex)
              }
            }}>
            <slot name="first-button-label" slot="first-button-label">
              ${this.firstButtonLabel || t('pager.first')}
            </slot>
            <slot name="previous-button-label" slot="previous-button-label">
              ${this.previousButtonLabel || t('pager.previous')}
            </slot>
            <slot name="next-button-label" slot="next-button-label">
              ${this.nextButtonLabel || t('pager.next')}
            </slot>
            <slot name="last-button-label" slot="last-button-label">
              ${this.lastButtonLabel || t('pager.last')}
            </slot>
          </dap-ds-pager>`
        : nothing}
    `
  }
}
